/**
 * @file Projectiles reels : deplacement, collision balayee (segment du tic vs cercles via grille), impact, expiration, retour au pool.
 */
import type { SimContext } from '../Context';
import { isShootable } from '../combat/damage';
import type { With } from '../ecs/World';
import { segmentCircleHit } from '../math/geometry';
import { BEHAVIORS } from '../weapons/behaviors';
import type { System } from './System';

const ARENA_MARGIN = 2;

const outsideArena = (ctx: SimContext, x: number, y: number): boolean => {
  const a = ctx.modes.rules.arena;
  if (a.shape === 'circle') return Math.hypot(x, y) > a.halfW + ARENA_MARGIN;
  if (a.shape === 'corridor') return Math.abs(x) > a.halfW + ARENA_MARGIN;
  return Math.abs(x) > a.halfW + ARENA_MARGIN || Math.abs(y) > a.halfH + ARENA_MARGIN;
};

/** Teste le segment parcouru pendant le tic ; retourne vrai si le projectile a ete consomme (detruit). */
const collide = (ctx: SimContext, e: With<'projectile'>, ax: number, ay: number): boolean => {
  const pr = e.projectile;
  const bx = e.transform.x;
  const by = e.transform.y;
  const module = BEHAVIORS.get(pr.behavior);
  for (const id of ctx.rt.targetGrid.querySegment(ax, ay, bx, by, pr.radius)) {
    const target = ctx.rt.world.get(id);
    if (!target || !isShootable(ctx, target) || pr.hitIds.includes(id)) continue;
    const t = segmentCircleHit(ax, ay, bx, by, target.transform.x, target.transform.y, target.collider.radius + pr.radius);
    const f = t < 0 ? 1 : t;
    e.transform.x = ax + (bx - ax) * f;
    e.transform.y = ay + (by - ay) * f;
    if (module.onHit(ctx, e, target).consumed) {
      ctx.rt.world.destroy(e.id);
      return true;
    }
  }
  e.transform.x = bx;
  e.transform.y = by;
  return false;
};

/**
 * Pour chaque projectile : duree de vie, mouvement (module.step ou rectiligne), collision balayee (sauf missiles et faisceaux),
 * expiration (portee, duree, hors arene) avec onExpire, plafond SIMULATION.limits.projectiles (les plus anciens sont retires).
 */
export const ProjectileSystem: System = {
  id: 'projectiles',
  update(ctx: SimContext, dt: number): void {
    const world = ctx.rt.world;
    const list = world.query('projectile', 'projectile');
    const overflow = list.length - ctx.config.sim.limits.projectiles;
    for (let i = 0; i < overflow; i++) {
      const old = list[i];
      if (old) world.destroy(old.id);
    }
    for (let i = Math.max(0, overflow); i < list.length; i++) {
      const e = list[i];
      if (!e || !e.alive) continue;
      const pr = e.projectile;
      pr.lifeLeft -= dt;
      if (pr.kind === 'beam') {
        if (pr.lifeLeft <= 0) world.destroy(e.id);
        continue;
      }
      const module = BEHAVIORS.get(pr.behavior);
      const ax = e.transform.x;
      const ay = e.transform.y;
      if (module.step) {
        module.step(ctx, e, dt);
      } else {
        e.transform.x += pr.vx * dt;
        e.transform.y += pr.vy * dt;
        pr.rangeLeft -= Math.hypot(pr.vx, pr.vy) * dt;
      }
      if (pr.kind !== 'missile' && collide(ctx, e, ax, ay)) continue;
      if (pr.rangeLeft <= 0 || pr.lifeLeft <= 0 || outsideArena(ctx, e.transform.x, e.transform.y)) {
        module.onExpire?.(ctx, e);
        world.destroy(e.id);
      }
    }
  },
};
